/* eslint-disable no-nested-ternary */
import * as React from 'react';
import { isEmpty } from 'ramda';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { CountryPrompt, Icon, Tooltip } from '../../components';
import {
  Country,
  getPaymentPlans,
  PaymentPlan,
} from '../../context/storeContext';
import useFetchAsync from '../../hooks/useFetchAsync';
import { paths } from '../../routes';
import getAppURL from '../../utils/getAppURL';
import getCookie from '../../utils/getCookie';
import getSubdomain from '../../utils/getSubdomain';
import isOnTestServer from '../../utils/isOnTestServer';
import setCookie from '../../utils/setCookie';

function Pricing() {
  const [country, setCountry] = React.useState<Country>({
    name: 'Ghana',
    slug: 'ghana',
    iso: 'GH',
    iso3: 'GHA',
    currency: 'GHS',
    currencySymbol: '₵',
    flag: '🇬🇭',
  });
  const [showCountryPrompt, setShowCountryPrompt] = React.useState(false);
  const { data, status, error, run } = useFetchAsync();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const subdomain = getSubdomain(window.location.origin);

  const store =
    searchParams.get('store') ||
    JSON.parse(getCookie('_pos_store') || '{}')?.name ||
    '';

  const isActive = location.pathname === paths.LANDING_PRICING_URL_PATH;

  let { plans } = (location.state || {}) as { plans: PaymentPlan[] };

  if (subdomain) {
    // For now any subdomain should be a logged in shop
    const host = window.location.origin.replace(`${subdomain}.`, 'www.');
    window.location.replace(`${host}${location.pathname}?store=${subdomain}`);
  }

  plans =
    !plans || plans.length === 0
      ? (
          (data || {}) as {
            paymentPlans: PaymentPlan[];
          }
        )?.paymentPlans
      : plans;

  const dashboardPath = React.useMemo(() => {
    const url = new URL(getAppURL());
    const { protocol, host } = url;

    if (!(store && !isEmpty(store))) {
      return '';
    }

    if (isOnTestServer()) {
      return `${protocol}//${host}/${store}/dashboard`;
    }
    return `${protocol}//${store}.${host.replace('www.', '')}/dashboard`;
  }, [store]);

  React.useEffect(() => {
    if (!plans || plans.length === 0) {
      const fetchingPaymentPlans = getPaymentPlans();
      run(fetchingPaymentPlans);
    }
  }, [plans, run]);

  React.useEffect(() => {
    document.body.style.backgroundColor = '#ffde00';

    const fndSavePrefCountry = JSON.parse(
      getCookie('_pos_pref_country') || '{}',
    );

    if (
      Object.keys(fndSavePrefCountry).length !== 0 &&
      fndSavePrefCountry?.country
    ) {
      setCountry(fndSavePrefCountry.country);
      setShowCountryPrompt(!fndSavePrefCountry.dontAskAgain);
    } else {
      setShowCountryPrompt(true);
    }
  }, []);

  const handleCountryPreference = async (
    fndCountry: Country,
    dontAskAgain: boolean,
  ) => {
    if (!fndCountry) {
      return;
    }

    setCountry(fndCountry);

    await setCookie(
      '_pos_pref_country',
      JSON.stringify({
        country: fndCountry,
        dontAskAgain,
      }),
      7,
    );
  };

  return (
    <>
      <div className="bg-pos-yellow p-6 tablet:p-10 laptop:p-6 h-screen flex flex-col justify-between">
        <div className="w-full laptop:container mx-auto">
          <header className="w-full">
            <div className="w-full flex items-center justify-between">
              <a
                className="flex items-center text-pos-black font-semibold text-2xl shrink-0"
                href={paths.LANDING_HOME_URL_PATH}
              >
                Swiftinflow
              </a>
              <div className="flex flex-row w-full justify-end content-center space-x-3 items-center">
                <a
                  className="hidden tablet:inline-flex items-center justify-center text-pos-hyperlink no-underline
                  text-center h-[40px] w-fit text-base font-normal hover:underline"
                  href="tel:+233549961446"
                >
                  +233 54 996 1446
                </a>
                <a
                  className={`hidden tablet:inline-flex items-center justify-center text-pos-black no-underline text-center
                  h-[35px] w-fit text-base font-medium ${
                    isActive ? '!bg-pos-black !text-pos-yellow' : ''
                  } hover:bg-pos-black hover:text-pos-yellow px-3 rounded-md`}
                  href={paths.LANDING_PRICING_URL_PATH}
                >
                  Pricing
                </a>
                {store && dashboardPath ? (
                  <a
                    className="inline-flex items-center justify-center text-pos-black border-2 border-pos-black rounded-md no-underline
                    hover:bg-pos-black hover:text-pos-yellow text-center h-[40px] w-fit px-3 text-base font-medium"
                    href={dashboardPath}
                  >
                    Dashboard
                  </a>
                ) : (
                  <>
                    <a
                      className="inline-flex items-center justify-center text-pos-black no-underline text-center
                      h-[35px] w-fit text-base font-medium hover:bg-pos-black hover:text-pos-yellow px-3 rounded-md"
                      href={paths.LOGIN_URL_PATH}
                    >
                      Login
                    </a>
                    <a
                      className="hidden tablet:inline-flex items-center justify-center text-pos-black border-2 border-pos-black rounded-md no-underline
                    hover:bg-pos-black hover:text-pos-yellow text-center h-[40px] w-fit px-3 text-base font-medium"
                      href={paths.REGISTER_URL_PATH}
                    >
                      Start free trial
                    </a>
                  </>
                )}
              </div>
            </div>
          </header>
          <div className="w-full laptop:w-[60%] mx-auto pt-16 tablet:pt-24 pb-10">
            <div className="flex justify-center flex-col">
              <h1
                className="text-3xl tablet:text-6xl text-pos-black font-bold 
                !leading-normal tablet:!leading-[1.3] text-center"
              >
                Enjoy unlimited access to all features
              </h1>

              {/* <p className="mt-5 tablet:mt-8 text-[#8f7319] leading-normal text-lg tablet:text-lg text-center">
                Enjoy unlimited access to all features.
              </p> */}
            </div>

            <div
              className="flex flex-row flex-wrap tablet:flex-nowrap space-y-8 tablet:space-y-0 tablet:space-x-10 
              justify-center w-full items-stretch mx-auto mt-10"
            >
              {(status === 'IDLE' || status === 'LOADING') && !plans ? (
                <div>
                  <span className="text-pos-black font-medium text-base">
                    Loading plans...
                  </span>
                </div>
              ) : plans && plans.length === 0 ? (
                <div>
                  <span className="text-pos-black font-medium whitespace-normal text-base">
                    Oops. Issue loading plans. Please contact our support team!
                  </span>
                </div>
              ) : (
                <>
                  {plans &&
                    plans
                      .filter(
                        fndPlan =>
                          fndPlan.name !== 'Free' &&
                          fndPlan.currency === country.currency,
                      )
                      .map((fndPlan, index) => (
                        <div
                          key={fndPlan.name}
                          className="flex flex-col bg-white items-center rounded-md 
                shadow-md border-[2px] border-pos-blue min-h-[400px] w-full tablet:w-[330px] px-8 pt-8 pb-6"
                        >
                          <div className="flex-1">
                            <h2 className="text-center font-semibold text-xl tablet:text-base text-pos-blue">
                              Quick Sales
                            </h2>

                            <p className="font-semibold text-xl text-center mt-3 text-pos-blue">
                              {country.currencySymbol}
                              {fndPlan.displayPrice}{' '}
                              <span className="text-pos-blue text-[13px] leading-none font-light">
                                /month
                              </span>
                            </p>

                            <p className="text-center mt-4 text-[15px]">
                              Full cloud-based app with POS, Inventory,
                              Reporting, Store location and more.
                            </p>

                            <div className="mt-6 space-y-2">
                              <p>
                                <Icon
                                  id="checkmark"
                                  className="w-3 h-auto inline-flex mr-2"
                                />
                                <span className="text-pos-black font-semibold">
                                  1 to {fndPlan.salesLimit}
                                </span>{' '}
                                sales/month incl.{' '}
                                <Tooltip
                                  content={`If you exceed the limit, additional ${fndPlan.metadata.additional.salesLimit} sales cost ${country.currencySymbol}${fndPlan.metadata.additional.displayPrice}.`}
                                />
                              </p>
                              <p>
                                <Icon
                                  id="checkmark"
                                  className="w-3 h-auto inline-flex mr-2"
                                />
                                <span className="text-pos-black font-semibold">
                                  1
                                </span>{' '}
                                store location{' '}
                              </p>
                              <p>
                                <Icon
                                  id="checkmark"
                                  className="w-3 h-auto inline-flex mr-2"
                                />
                                <span className="text-pos-black font-semibold">
                                  Unlimited
                                </span>{' '}
                                inventory{' '}
                              </p>
                              <p>
                                <Icon
                                  id="checkmark"
                                  className="w-3 h-auto inline-flex mr-2"
                                />
                                <span className="text-pos-black font-semibold">
                                  Unlimited
                                </span>{' '}
                                features{' '}
                              </p>
                              <p>
                                <Icon
                                  id="checkmark"
                                  className="w-3 h-auto inline-flex mr-2"
                                />
                                <span className="text-pos-black font-semibold">
                                  Unlimited
                                </span>{' '}
                                support{' '}
                                <Tooltip content="For local businesses we're here for you 24/7." />
                              </p>
                            </div>
                          </div>

                          <div className="h-fit mt-10 text-center">
                            <span className="text-xbase mb-2 inline-block">
                              Save 50% for 3 months
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                navigate(paths.REGISTER_URL_PATH, {
                                  state: { plan: fndPlan },
                                })
                              }
                              className="bg-pos-blue border border-[rgba(27,31,36,0.15)] 
                          inline-flex px-10 py-3 text-white text-xbase leading-none rounded-md cursor-pointer"
                            >
                              Pay Now
                            </button>
                          </div>
                        </div>
                      ))}

                  <div
                    className="flex flex-col bg-white items-center rounded-md 
                shadow-md border-[1px] border-pos-header-bg min-h-[400px] w-full tablet:w-[330px] px-8 pt-8 pb-6"
                  >
                    <div className="flex-1">
                      <h2 className="text-center font-semibold text-xl tablet:text-base">
                        Omega Sales
                      </h2>

                      <p className="font-semibold text-xl text-center mt-3">
                        {country.currencySymbol}🤔{' '}
                        <span className="text-pos-gray text-[13px] leading-none font-light">
                          /month
                        </span>
                      </p>

                      <p className="text-center text-[15px] mt-4">
                        Full cloud-based app with POS, Inventory, Reporting,
                        Multiple Stores and more.
                      </p>

                      <div className="mt-6 space-y-2">
                        <p>
                          <Icon
                            id="checkmark"
                            className="w-3 h-auto inline-flex mr-2"
                          />
                          <span className="text-pos-black font-semibold">
                            Your
                          </span>{' '}
                          sales/month incl.{' '}
                        </p>
                        <p>
                          <Icon
                            id="checkmark"
                            className="w-3 h-auto inline-flex mr-2"
                          />
                          <span className="text-pos-black font-semibold">
                            Your
                          </span>{' '}
                          store locations{' '}
                        </p>
                        <p>
                          <Icon
                            id="checkmark"
                            className="w-3 h-auto inline-flex mr-2"
                          />
                          <span className="text-pos-black font-semibold">
                            Unlimited
                          </span>{' '}
                          inventory{' '}
                        </p>
                        <p>
                          <Icon
                            id="checkmark"
                            className="w-3 h-auto inline-flex mr-2"
                          />
                          <span className="text-pos-black font-semibold">
                            Unlimited
                          </span>{' '}
                          features{' '}
                        </p>
                        <p>
                          <Icon
                            id="checkmark"
                            className="w-3 h-auto inline-flex mr-2"
                          />
                          <span className="text-pos-black font-semibold">
                            Unlimited
                          </span>{' '}
                          support{' '}
                          <Tooltip content="For local businesses we're here for you 24/7." />
                        </p>
                      </div>
                    </div>

                    <div className="h-fit mt-10 text-center">
                      <span className="text-xbase mb-2 inline-block">
                        Need a bigger plan?{' '}
                        <Tooltip content="Call us if your want to handle bigger sales at multiple store locations." />
                      </span>
                      <a
                        href="tel:+233549961446"
                        className="bg-pos-blue border border-[rgba(27,31,36,0.15)] 
                          inline-flex px-10 py-3 text-white text-xbase leading-none rounded-md cursor-pointer"
                      >
                        Call us
                      </a>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* <div className="bg-white px-10 py-12 rounded-md shadow-md border-[1px] border-pos-header-bg w-full flex flex-row flex-wrap">
              <div className="bg-white rounded-md border overflow-x-auto border-[#d0d7de] w-full laptop:w-[65%]">
                <table className="border-collapse table-auto w-full h-full bg-white table-striped rounded-md block tablet:table">
                  <thead className="hidden tablet:table-header-group">
                    <tr className="text-left bg-pos-header-bg sticky top-0 z-10">
                      <th scope="col">Products per month</th>
                      <th scope="col">Products per month</th>
                      <th scope="col">No. of Sales</th>
                      <th scope="col">Additional sales</th>
                      <th scope="col">Monthly price</th>
                      <th scope="col">&nbsp;</th>
                    </tr>
                  </thead>
                  {(status === 'IDLE' || status === 'LOADING') && !plans ? (
                    <tbody className="block tablet:table-row-group w-full">
                      <tr className="w-full h-full bg-[#f2f2f2] bg-opacity-75 transition-opacity text-pos-black block tablet:table-row">
                        <td
                          colSpan={5}
                          className="text-center block tablet:table-cell"
                        >
                          <span className="text-pos-black font-medium">
                            Loading plans...
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  ) : plans && plans.length === 0 ? (
                    <tbody className="block tablet:table-row-group w-full">
                      <tr className="w-full h-full bg-[#f2f2f2] bg-opacity-75 transition-opacity text-pos-black block tablet:table-row">
                        <td
                          colSpan={5}
                          className="text-center block tablet:table-cell"
                        >
                          <span className="text-pos-black font-medium whitespace-normal">
                            Oops. Issue loading plans. Please contact our
                            support team!
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  ) : (
                    <tbody className="block tablet:table-row-group">
                      {plans
                        .filter(
                          fndPlan =>
                            fndPlan.name !== 'Free' &&
                            fndPlan.currency === country.currency,
                        )
                        .map((fndPlan, index) => (
                          <tr
                            key={fndPlan.name}
                            className="block tablet:table-row"
                          >
                            <td className="block tablet:table-cell">
                              <div className="flex flex-row w-full font-semibold text-pos-black">
                                <Icon
                                  id="iconProductNav"
                                  className="h-auto w-5 object-cover fill-pos-gray mr-2"
                                />
                                {fndPlan.productsLimit.toLocaleString()}
                                <span className="tablet:hidden whitespace-pre">
                                  {' '}
                                  Products
                                </span>
                              </div>
                            </td>
                            <td className="block tablet:table-cell whitespace-normal">
                              <span className="text-sm font-medium">
                                with up to
                              </span>{' '}
                              {fndPlan.salesLimit.toLocaleString()}
                              <span className="tablet:hidden">
                                {' '}
                                Sales{' '}
                                <span className="text-sm font-medium">
                                  (added {country.currencySymbol}
                                  {
                                    fndPlan.metadata.additional.displayPrice
                                  } per {fndPlan.metadata.additional.salesLimit}{' '}
                                  sales )
                                </span>
                              </span>
                            </td>
                            <td className="hidden tablet:table-cell whitespace-normal">
                              + {country.currencySymbol}
                              {
                                fndPlan.metadata.additional.displayPrice
                              } per {fndPlan.metadata.additional.salesLimit}
                            </td>
                            <td className="block">
                              {country.currencySymbol}
                              {fndPlan.displayPrice}
                              <span className="tablet:hidden whitespace-pre">
                                {' '}
                                per month{' '}
                                <div className="inline-block text-xl animate-bounce">
                                  👈
                                </div>
                              </span>
                            </td>
                            <td className="block tablet:table-cell">
                              <div className="text-center tablet:text-left">
                                <button
                                  type="button"
                                  onClick={() =>
                                    navigate(paths.REGISTER_URL_PATH, {
                                      state: { plan: fndPlan },
                                    })
                                  }
                                  className="bg-pos-blue border border-[rgba(27,31,36,0.15)] 
                          inline-flex px-8 py-3 tablet:py-2 tablet:px-3 text-white text-[13px] leading-none rounded-md cursor-pointer"
                                >
                                  Pay Now
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}

                      <tr className="block tablet:table-row">
                        <td className="block tablet:table-cell" colSpan={5}>
                          <div className="whitespace-normal">
                            <h4 className="text-base text-pos-black font-semibold">
                              Need to sell more products?
                            </h4>
                            <p className="text-xbase text-pos-gray font-medium">
                              <a
                                className="text-pos-hyperlink no-underline hover:underline"
                                href="tel:+233549961446"
                              >
                                Call us
                              </a>{' '}
                              and we’d be happy to discuss the details with you!
                            </p>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  )}
                </table>
              </div>
              <div className="hidden tablet:flex flex-col items-center justify-center w-[35%] pl-10">
                <h2 className="text-pos-black font-semibold text-2xl mb-3">
                  Ready to get started?
                </h2>

                <div className="text-center w-full mb-6">
                  We’ll start you off with 50% discount for 3 months if you{' '}
                  <strong>PAY NOW</strong> for any of our plans.
                </div>

                <div className="text-6xl animate-bounce">👈</div>
              </div>
            </div> */}
          </div>
        </div>
      </div>

      {showCountryPrompt && (
        <CountryPrompt
          onCloseModal={() => setShowCountryPrompt(false)}
          onSelectedCountry={handleCountryPreference}
          defaultCountry={{
            label: `${country?.flag} ${country.name}`,
            value: country.iso,
          }}
        />
      )}
    </>
  );
}

export default Pricing;
